import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import type {
  SecurityEventOutcome,
  SecurityEventSource,
} from "../shared/types";
import { adminQueries, type SecurityEventQueryFilters } from "./admin-query";
import {
  CloudflareStatusBadge,
  EmptyState,
  PageHeader,
  PanelState,
  RefreshFailedNotice,
} from "./AdminUi";
import { formatDate } from "./admin-model";
import { Icon } from "./Icon";

type SourceFilter = "all" | SecurityEventSource;
type OutcomeFilter = "all" | SecurityEventOutcome;
type TimeRange = "all" | "24h" | "7d" | "30d" | "90d";

const RANGE_SECONDS: Record<Exclude<TimeRange, "all">, number> = {
  "24h": 24 * 60 * 60,
  "7d": 7 * 24 * 60 * 60,
  "30d": 30 * 24 * 60 * 60,
  "90d": 90 * 24 * 60 * 60,
};

export function AuditPanel() {
  const [actor, setActor] = useState("");
  const [actorFilter, setActorFilter] = useState("");
  const [source, setSource] = useState<SourceFilter>("all");
  const [outcome, setOutcome] = useState<OutcomeFilter>("all");
  const [timeRange, setTimeRange] = useState<TimeRange>("all");
  const [since, setSince] = useState<number>();

  useEffect(() => {
    const timeout = window.setTimeout(
      () => setActorFilter(actor.trim().toLowerCase()),
      300,
    );
    return () => window.clearTimeout(timeout);
  }, [actor]);

  const filters = useMemo<SecurityEventQueryFilters>(
    () => ({
      ...(actorFilter ? { actor: actorFilter } : {}),
      ...(source !== "all" ? { source } : {}),
      ...(outcome !== "all" ? { outcome } : {}),
      ...(since !== undefined ? { since } : {}),
    }),
    [actorFilter, outcome, since, source],
  );
  const security = useInfiniteQuery(adminQueries.securityInfinite(25, filters));
  const events = useMemo(() => {
    const unique = new Map(
      (security.data?.pages ?? [])
        .flatMap((page) => page.events)
        .map((event) => [event.id, event]),
    );
    return [...unique.values()];
  }, [security.data?.pages]);

  // Only a failure with nothing cached is fatal; a failed background refetch
  // keeps the panel on screen.
  if (!security.data) {
    return <PanelState error={security.isError} label="security events" />;
  }

  const cloudflare = security.data.pages[0]!.cloudflare;
  const hasMore = Boolean(security.hasNextPage);
  const loadingMore = security.isFetchingNextPage;
  const onLoadMore = () => void security.fetchNextPage();
  const hasActiveFilters =
    Boolean(actorFilter) ||
    source !== "all" ||
    outcome !== "all" ||
    timeRange !== "all";

  function changeTimeRange(value: TimeRange) {
    setTimeRange(value);
    setSince(
      value === "all"
        ? undefined
        : Math.floor(Date.now() / 1000) - RANGE_SECONDS[value],
    );
  }

  function clearFilters() {
    setActor("");
    setActorFilter("");
    setSource("all");
    setOutcome("all");
    setTimeRange("all");
    setSince(undefined);
  }

  return (
    <>
      <PageHeader
        action={<CloudflareStatusBadge state={cloudflare.state} />}
        description="Sign-in activity and redacted configuration changes. Credentials and authentication artifacts are never recorded."
        eyebrow="Security history"
        title="Audit log"
      />
      {security.isError && <RefreshFailedNotice />}
      <section className="directory-card">
        <header className="collection-header">
          <div>
            <h2>Recent activity</h2>
            <p>Sign-ins and administrative changes, newest first.</p>
          </div>
          <span>{events.length}</span>
        </header>
        <form
          className="audit-filters"
          onSubmit={(event) => {
            event.preventDefault();
            setActorFilter(actor.trim().toLowerCase());
          }}
        >
          <label className="search-field audit-filters__search">
            <span className="sr-only">Search by actor email</span>
            <Icon name="search" size={17} />
            <input
              aria-label="Search by actor email"
              onChange={(event) => setActor(event.target.value)}
              placeholder="Search actor email…"
              type="search"
              value={actor}
            />
          </label>
          <div className="audit-filters__selects">
            <label className="select-field select-field--compact">
              <span className="sr-only">Filter by source</span>
              <select
                aria-label="Filter by source"
                onChange={(event) =>
                  setSource(event.target.value as SourceFilter)
                }
                value={source}
              >
                <option value="all">All sources</option>
                <option value="cloudflare-access">Cloudflare Access</option>
                <option value="identity-provider">Identity provider</option>
              </select>
            </label>
            <label className="select-field select-field--compact">
              <span className="sr-only">Filter by outcome</span>
              <select
                aria-label="Filter by outcome"
                onChange={(event) =>
                  setOutcome(event.target.value as OutcomeFilter)
                }
                value={outcome}
              >
                <option value="all">All outcomes</option>
                <option value="allowed">Allowed</option>
                <option value="denied">Denied</option>
                <option value="succeeded">Succeeded</option>
                <option value="failed">Failed</option>
                <option value="recorded">Recorded</option>
              </select>
            </label>
            <label className="select-field select-field--compact">
              <span className="sr-only">Filter by time</span>
              <select
                aria-label="Filter by time"
                onChange={(event) =>
                  changeTimeRange(event.target.value as TimeRange)
                }
                value={timeRange}
              >
                <option value="all">All time</option>
                <option value="24h">Last 24 hours</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
              </select>
            </label>
            <button
              className="button button--quiet button--compact"
              disabled={!hasActiveFilters}
              onClick={clearFilters}
              type="button"
            >
              Clear
            </button>
          </div>
          {security.isFetching && !loadingMore && (
            <span className="audit-filters__progress" role="status">
              Updating results…
            </span>
          )}
        </form>
        <div className="table-wrap">
          <table className="audit-table">
            <caption className="sr-only">
              Authentication and administrative audit events
            </caption>
            <thead>
              <tr>
                <th>Time</th>
                <th>Outcome</th>
                <th>Source</th>
                <th>Actor</th>
                <th>Application / target</th>
                <th>Context</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id}>
                  <td className="muted" data-label="Time">
                    <time
                      dateTime={new Date(event.createdAt * 1000).toISOString()}
                    >
                      {formatDate(event.createdAt)}
                    </time>
                  </td>
                  <td data-label="Outcome">
                    <span
                      className={`event-outcome event-outcome--${event.outcome}`}
                    >
                      {event.outcome}
                    </span>
                  </td>
                  <td data-label="Source">
                    {event.source === "cloudflare-access"
                      ? "Cloudflare Access"
                      : "Identity provider"}
                  </td>
                  <td data-label="Actor">{event.actorEmail}</td>
                  <td data-label="Application / target">
                    <strong>{event.applicationDomain ?? event.targetId}</strong>
                    <small>{event.applicationId ?? event.targetType}</small>
                  </td>
                  <td data-label="Context">
                    <span>{event.summary}</span>
                    {(event.ipAddress || event.country || event.rayId) && (
                      <small className="event-context">
                        {[event.ipAddress, event.country, event.rayId]
                          .filter(Boolean)
                          .join(" · ")}
                      </small>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!events.length && (
            <EmptyState
              hint={
                hasActiveFilters
                  ? "Try changing or clearing the current filters."
                  : "Authentication and configuration activity appears here."
              }
              onClearFilters={hasActiveFilters ? clearFilters : undefined}
              title={
                hasActiveFilters ? "No matching events" : "No audit events"
              }
            />
          )}
        </div>
        <footer className="directory-footer pagination-footer">
          <span>
            {events.length} event{events.length === 1 ? "" : "s"} loaded
          </span>
          {hasMore ? (
            <button
              className="button button--secondary button--compact"
              disabled={loadingMore}
              onClick={onLoadMore}
              type="button"
            >
              {loadingMore ? "Loading…" : "Load older activity"}
            </button>
          ) : (
            <span>All available activity loaded</span>
          )}
        </footer>
        <p className="collection-footnote">
          Secrets, tokens, codes, state, and passwords are excluded.
        </p>
      </section>
    </>
  );
}
